import type { ConversationsListFilter } from '@repo/api/chat/chatService';

import { useDebounceValue } from '@repo/hooks/useDebounceValue';
import { useInfiniteQueryResult } from '@repo/hooks/useInfiniteQueryResult';
import { SearchIcon, Settings2Icon } from 'lucide-react';
import { useState } from 'react';

import { ConversationsListItem, ConversationsListSkeletonItem } from '@/components/features/chat/layout/ConversationsListItem';
import { Button, ButtonGroup } from '@/components/ui/button';
import { InfiniteScroll } from '@/components/ui/infinite-scroll';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle';
import { useGetLastConversationsInfinite } from '@/hooks/chat';
import { useLocalStorage } from '@/hooks/local-storage';
import { useCredentials } from '@/store/auth-store';
import { useChatStore } from '@/store/chat-store';

interface ChatConversationsListProps {
  openCreateConversation: () => void;
}

export default function ConversationsList(chatConversationsListProps: ChatConversationsListProps) {
  const { openCreateConversation } = chatConversationsListProps;
  const [searchValue, setSearchValue] = useState<string>('');
  const [filterType, setFilterType] = useLocalStorage<string | undefined>('chat-list-filter-type', undefined);
  const chatId = useChatStore((state) => state.chatId);
  const credentials = useCredentials();
  const searchDebouncedValue = useDebounceValue(searchValue, 400);

  const { data, isLoading, isFetching, isError, hasNextPage, fetchNextPage } = useGetLastConversationsInfinite({
    currentChatId: chatId,
    user: credentials,
    name: searchDebouncedValue ? searchDebouncedValue : undefined,
    type: filterType == 'all' ? undefined : (filterType as ConversationsListFilter),
  });

  const { data: conversations, isEmpty: conversationsIsEmpty, noResults: conversationsNoResults } = useInfiniteQueryResult(data, searchDebouncedValue);

  const next = async () => {
    await fetchNextPage();
  };

  return (
    <section className={'md:bg-background flex min-h-0 w-full min-w-0 md:min-w-76 md:overflow-hidden md:rounded-2xl md:border lg:w-76'}>
      <div className="flex w-full flex-col md:min-h-0 md:flex-1">
        <div className={'bg-background-dark sticky top-0 z-10 p-4 md:border-b md:bg-transparent'}>
          <ButtonGroup className="w-full">
            <InputGroup className={'w-full'}>
              <InputGroupInput value={searchValue} onChange={(e) => setSearchValue(e.target.value)} placeholder="Wyszukaj..." />
              <InputGroupAddon align="inline-start">
                <SearchIcon className="text-muted-foreground" />
              </InputGroupAddon>
            </InputGroup>
            <Button type="button" onClick={openCreateConversation} variant="outline" size="md">
              <Settings2Icon />
              <span className="sr-only">Utwórz konwersację</span>
            </Button>
          </ButtonGroup>
        </div>
        <div className={'flex flex-col gap-2 p-4 md:min-h-0 md:flex-1 md:overflow-y-auto'}>
          <ToggleGroup type={'single'} variant={'outline'} value={filterType} onValueChange={setFilterType} groupVariant={'separate'} size={'sm'} className={'w-full'}>
            <ToggleGroupItem value={'all'} onClick={() => setFilterType('mixed')} className={'w-full'}>
              Wszystko
            </ToggleGroupItem>
            <ToggleGroupItem value={'individual'} onClick={() => setFilterType('user')} className={'w-full'}>
              Użytkownicy
            </ToggleGroupItem>
            <ToggleGroupItem value={'group'} onClick={() => setFilterType('group')} className={'w-full'}>
              Grupy
            </ToggleGroupItem>
          </ToggleGroup>
          <ul>
            {isLoading && (
              <>
                <ConversationsListSkeletonItem />
                <ConversationsListSkeletonItem />
                <ConversationsListSkeletonItem />
                <ConversationsListSkeletonItem />
              </>
            )}
            {conversationsNoResults && !isFetching ? (
              <div className={'flex flex-col items-center gap-4 py-11'}>
                <h5 className={'text-muted-foreground text-center text-sm italic'}>Brak konwersacji o podanej nazwie</h5>
              </div>
            ) : null}
            {conversationsIsEmpty && !isFetching ? (
              <div className={'flex flex-col items-center gap-4 py-11'}>
                <h5 className={'text-muted-foreground text-center text-sm italic'}>Brak konwersacji</h5>
              </div>
            ) : null}
            {conversations.map((conversation) => (
              <ConversationsListItem key={conversation.id} {...conversation} />
            ))}
            <InfiniteScroll hasNextPage={hasNextPage} isLoading={isLoading} isFetching={isFetching} next={next} threshold={0.65}>
              {hasNextPage && !isError && <ConversationsListSkeletonItem />}
            </InfiniteScroll>
          </ul>
        </div>
      </div>
    </section>
  );
}
